<template>
  <div :class="paddingClass">
    <font-awesome-layers
      full-width
      :id="key"
      v-tooltip.top="{
        value: tooltipTitle,
        disabled: tooltipDisabled,
      }"
    >
      <i class="fa-kit fa-solid-bolt-circle-pause"></i>
      <font-awesome-icon :icon="icon" :style="{ color: styledColor }" />
      <font-awesome-layers-text
        v-if="totalCount"
        counter
        :value="totalCount"
        transform="shrink-8 right-24 down-6"
        :style="{
          backgroundColor: styledColor,
        }"
      />
      <font-awesome-layers-text
        v-if="asterisk"
        transform="right-14 up-4"
        value="*"
      />
      <font-awesome-icon
        v-if="snoozedUntil"
        icon="clock"
        transform="shrink-4 down-6 right-14"
        style="color: grey"
      />
    </font-awesome-layers>
  </div>
</template>

<script lang="ts">
import Colors from '@/infrastructure/support/Colors.ts';
import { formatAsDate } from '@/infrastructure/support/utils/DateTimeUtils.ts';
import IssueValues from '@/presentation/components/status/IssueValues.js';
import {
  issueShortDescription,
  issueLongDescription,
} from '@/presentation/components/status/IssueDescriptions.ts';
import MeterIssueApi from '@/data/meters/MeterIssueApi.ts';

export default {
  data() {
    return {
      id: (Math.random() * 1e10).toFixed(0),
    };
  },
  created() {
    new MeterIssueApi().getAllTypes();
  },
  props: {
    type: { type: String, required: true },
    values: { type: Object },
    severity: { type: String },
    color: { type: String },
    showTooltip: { type: Boolean },
    showExtendedTooltip: { type: Boolean },
    showFilterTooltip: { type: Boolean },
    pluralize: { type: Boolean, default: () => false },
    asterisk: { type: Boolean, default: () => false },
    snoozedUntil: { type: String },
    totalCount: { type: Number },
  },
  methods: {
    getValuesForIssueType(type) {
      if (!this.values) {
        const values = IssueValues.issues();
        return Object.hasOwn(values, type) ? values[type] : null;
      } else {
        return Object.hasOwn(this.values, type) ? this.values[type] : null;
      }
    },
  },
  computed: {
    key() {
      return this.type + this.id;
    },
    styledColor() {
      if (this.snoozedUntil && !this.totalCount) {
        return 'gray';
      }
      return this.color
        ? this.color
        : this.severity
          ? Colors.colorByStatus(this.severity)
          : 'black';
    },
    paddingClass() {
      return this.snoozedUntil || this.totalCount ? 'pr-3' : null;
    },
    icon() {
      const values = this.getValuesForIssueType(this.type);
      return values ? values.icon : 'exclamation-triangle';
    },
    iconClass() {
      const values = this.getValuesForIssueType(this.type);
      return values ? values.iconClass : null;
    },
    tooltipDisabled() {
      return (
        (!this.showExtendedTooltip &&
          !this.showTooltip &&
          !this.showFilterTooltip) ||
        this.tooltipTitle === null
      );
    },
    tooltipTitle() {
      const values = this.getValuesForIssueType(this.type);
      let tooltip = '';

      if (!values) return null;

      if (this.totalCount) {
        tooltip =
          this.totalCount +
          ' ' +
          this.$t('features.plant_issues.tooltip_occurrence') +
          ' ';
      }

      if (this.showFilterTooltip || !this.showExtendedTooltip) {
        tooltip += issueShortDescription(this.type, values.tooltip);
      } else {
        const longDescription = issueLongDescription(
          this.type,
          values.extendedTooltip,
        );
        tooltip += this.pluralize
          ? longDescription.replace(
              this.$t('glossary.plant', 1) + ' ',
              this.$t('glossary.plant', 2) + ' ',
            )
          : longDescription;
      }

      if (this.snoozedUntil) {
        const snoozedUntilDate = formatAsDate(new Date(this.snoozedUntil));

        if (this.totalCount) {
          tooltip +=
            ' ' +
            this.$t('features.plant_issues.tooltip_with_snoozed') +
            ' ' +
            snoozedUntilDate;
        } else {
          tooltip +=
            ' (' +
            this.$t('glossary.snoozed_until') +
            ' ' +
            snoozedUntilDate +
            ')';
        }
      }

      return tooltip;
    },
  },
};
</script>
